import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import { ServiceDetail } from "~/components/inside/services/service-detail";
import { usePageEngagement } from "~/hooks/use-page-engagement";
import { publicCacheHeaders } from "~/lib/cache-headers";
import { LEGACY_SERVICE_REDIRECTS } from "~/lib/constants/capabilities";
import {
  getServicePage,
  isServicePageSlug,
} from "~/lib/constants/service-pages";
import { seo } from "~/lib/seo";
import {
  generateStructuredDataGraph,
  getServiceDetailPageSchemas,
} from "~/lib/structured-data";
import { localizeHref } from "~/paraglide/runtime.js";

/**
 * Two kinds of URL share this segment.
 *
 * The dedicated service pages (`SERVICE_PAGE_SLUGS`) render here: one page per
 * cluster of buyer searches — AI agents, RAG, n8n, AI integration.
 *
 * The five service pages the site carried while it sold booking platforms were
 * indexed and linked, so those URLs keep working: each one redirects
 * permanently to the capability on /services that now covers it, or to the page
 * itself where nothing corresponds. Any other slug is a 404, not a redirect —
 * it never existed.
 *
 * `localizeHref` rather than a bare `to`: the router only ever sees
 * de-localized paths, so without it /lv/services/<slug> would land a Latvian
 * visitor on the English page. One rule covers both locales.
 */
export const Route = createFileRoute("/(public)/services/$serviceSlug")({
  beforeLoad: ({ params }) => {
    if (isServicePageSlug(params.serviceSlug)) {
      return;
    }

    // `Object.hasOwn` before the read: the slug is untrusted URL input, and a
    // bare index would resolve inherited keys — /services/constructor and
    // /services/__proto__ would 301 to a garbage anchor instead of 404ing.
    if (!Object.hasOwn(LEGACY_SERVICE_REDIRECTS, params.serviceSlug)) {
      throw notFound();
    }

    const anchor = LEGACY_SERVICE_REDIRECTS[params.serviceSlug];

    throw redirect({
      href: localizeHref(anchor ? `/services#${anchor}` : "/services"),
      statusCode: 301,
      throw: true,
    });
  },
  component: ServiceDetailPage,
  head: ({ params }) => {
    // beforeLoad has already turned every other slug into a redirect or a 404.
    if (!isServicePageSlug(params.serviceSlug)) {
      return {};
    }

    const page = getServicePage(params.serviceSlug);
    const seoData = seo({
      description: page.meta.description,
      keywords: page.keywords,
      path: `/services/${page.slug}`,
      title: page.meta.title,
    });
    const structuredData = generateStructuredDataGraph(
      getServiceDetailPageSchemas(page)
    );
    return {
      links: seoData.links,
      meta: seoData.meta,
      scripts: [
        {
          children: structuredData,
          type: "application/ld+json",
        },
      ],
    };
  },
  headers: publicCacheHeaders,
});

function ServiceDetailPage() {
  const { serviceSlug } = Route.useParams();
  usePageEngagement({ pagePath: `/services/${serviceSlug}` });

  if (!isServicePageSlug(serviceSlug)) {
    return null;
  }

  return <ServiceDetail page={getServicePage(serviceSlug)} />;
}
